// â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
// VÂ·IANet Governance â€” useFlowData Hook (M3: VÂ·IANet Flow)
// â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•

import { useState, useCallback, useEffect } from 'react';
import { MarkerType, type Viewport } from '@xyflow/react';
import { request } from '../../../../shared/services/api';
import type { Flujograma, FlowNode, FlowEdge } from '../types/flowchart.types';

// â”€â”€â”€ Clean React Flow internals before sending to API â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

function cleanForApi(nodes: FlowNode[], edges: FlowEdge[]) {
  return {
    nodes: nodes.map((n: any) => {
      // Strip all React Flow internal/transient properties
      const {
        measured,
        selected,
        dragging,
        resizing,
        internals,
        handleBounds,
        width,
        height,
        ...rest
      } = n;
      return rest;
    }),
    edges: edges.map((e: any) => {
      // Strip React Flow internal properties + marker objects (reconstructed from data on load)
      const { selected, markerEnd, markerStart, sourceNode, targetNode, internals, ...rest } = e;
      return {
        ...rest,
        data: {
          ...(rest.data || {}),
          arrowType: typeof e.data?.arrowType === 'string' ? e.data.arrowType : 'arrow',
          lineStyle: e.data?.lineStyle || 'solid',
        },
      };
    }),
  };
}

/** Ensure all edges loaded from API have type 'orthogonal' and restore markers from data.arrowType.
 *  cleanForApi strips markerEnd/markerStart before saving. On load, we restore them
 *  so React Flow can create SVG <defs> and pass resolved URL strings to OrthogonalEdge. */
export function normalizeEdges(edges: FlowEdge[]): FlowEdge[] {
  return edges.map((e) => {
    const d = (e.data as Record<string, unknown>) || {};
    const arrowType = (d.arrowType as string) || 'arrow';
    const color = (d.color as string) || undefined;

    const normalized: FlowEdge = { ...e, type: e.type || 'orthogonal' };

    // Restore markers based on arrowType (stripped by cleanForApi before save)
    if (arrowType === 'none') {
      normalized.markerEnd = undefined;
      normalized.markerStart = undefined;
    } else if (arrowType === 'bidirectional') {
      normalized.markerEnd = {
        type: MarkerType.ArrowClosed,
        ...(color ? { color } : {}),
        width: 14,
        height: 14,
      };
      normalized.markerStart = {
        type: MarkerType.ArrowClosed,
        ...(color ? { color } : {}),
        width: 14,
        height: 14,
      };
    } else if (arrowType === 'open') {
      normalized.markerEnd = {
        type: MarkerType.Arrow,
        ...(color ? { color } : {}),
        width: 14,
        height: 14,
      };
      normalized.markerStart = undefined;
    }
    // For 'arrow' (default): don't set markerEnd here â€” defaultEdgeOptions provides it

    return normalized;
  });
}

// â”€â”€â”€ API Functions â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

function fetchBorrador(processId: string) {
  return request<{ data: Flujograma | null }>(`/flujogramas/${processId}/draft`);
}

function fetchPublicado(processId: string) {
  return request<{ data: Flujograma | null }>(`/flujogramas/${processId}/published`);
}

function fetchUltimo(processId: string) {
  return request<{ data: Flujograma | null }>(`/flujogramas/${processId}/latest`);
}

function crearFlujogramaApi(
  processId: string,
  contenido: {
    nodes: FlowNode[];
    edges: FlowEdge[];
    viewport: Viewport;
    metadata?: Record<string, unknown>;
  },
) {
  return request<{ data: Flujograma }>(`/flujogramas/${processId}`, {
    method: 'POST',
    body: JSON.stringify({ contenido }),
  });
}

function actualizarFlujograma(
  flowchartId: string,
  contenido: {
    nodes: FlowNode[];
    edges: FlowEdge[];
    viewport: Viewport;
    metadata?: Record<string, unknown>;
  },
  changelog?: string,
) {
  return request<{ data: Flujograma }>(`/flujogramas/edit/${flowchartId}`, {
    method: 'PUT',
    body: JSON.stringify({ contenido, changelog }),
  });
}

function enviarRevisionApi(flowchartId: string) {
  // 0630 ADR-025: usa el endpoint unificado /transitar (motor) en lugar del
  // legacy /submit-review. El motor delega al service legacy y dispara los
  // hooks Notify+Sign unificados.
  return request<{ data: Flujograma }>(`/flujogramas/edit/${flowchartId}/transitar`, {
    method: 'POST',
    body: JSON.stringify({ accion: 'ENVIAR_REVISION' }),
  });
}

function fetchVersiones(processId: string) {
  return request<{ data: FlowVersion[] }>(`/flujogramas/${processId}/versions`);
}

function fetchVersion(processId: string, version: number) {
  return request<{ data: Flujograma }>(`/flujogramas/${processId}/versions/${version}`);
}

function crearNuevaEdicion(processId: string) {
  return request<{ data: Flujograma }>(`/flujogramas/${processId}/new-edition`, { method: 'POST' });
}

export interface FlowVersion {
  id: string;
  version: number;
  estado: string;
  changelog?: string | null;
  creadoPor?: string | null;
  aprobadoPor?: string | null;
  fechaCreacion: string;
  fechaAprobacion?: string | null;
  fechaPublicacion?: string | null;
}

// â”€â”€â”€ Hook â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

export interface UseFlowDataReturn {
  flujograma: Flujograma | null;
  loading: boolean;
  error: string | null;
  readOnly: boolean;
  versions: FlowVersion[];
  selectedVersionIndex: number;
  guardarBorrador: (
    nodes: FlowNode[],
    edges: FlowEdge[],
    viewport: Viewport,
    changelog?: string,
    metadata?: Record<string, unknown>,
  ) => Promise<void>;
  crearFlujograma: (
    nodes: FlowNode[],
    edges: FlowEdge[],
    viewport: Viewport,
    metadata?: Record<string, unknown>,
  ) => Promise<void>;
  enviarRevision: () => Promise<void>;
  selectVersion: (index: number) => Promise<void>;
  nuevaEdicion: () => Promise<void>;
  recargar: () => Promise<void>;
}

export function useFlowData(procesoId: string): UseFlowDataReturn {
  const [flujograma, setFlujograma] = useState<Flujograma | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [readOnly, setReadOnly] = useState(false);
  const [versions, setVersions] = useState<FlowVersion[]>([]);
  const [selectedVersionIndex, setSelectedVersionIndex] = useState(0);

  const cargarVersiones = useCallback(async () => {
    if (!procesoId) return;
    try {
      const res = await fetchVersiones(procesoId);
      setVersions(res.data ?? []);
    } catch {
      // Non-critical — versions list is optional
    }
  }, [procesoId]);

  const cargar = useCallback(async () => {
    if (!procesoId) return;
    setLoading(true);
    setError(null);
    try {
      // Load versions list
      await cargarVersiones();

      // First try draft (protected — 404 or network error falls through to published)
      try {
        const draftRes = await fetchBorrador(procesoId);
        if (draftRes.data) {
          setFlujograma(draftRes.data);
          setReadOnly(false);
          return;
        }
      } catch {
        // No draft available — fall through to published
      }
      // Fallback 1: published as read-only base
      try {
        const pubRes = await fetchPublicado(procesoId);
        if (pubRes.data) {
          setFlujograma(pubRes.data);
          setReadOnly(true);
          return;
        }
      } catch {
        // Published not found, try latest
      }
      // Fallback 2: latest active (EN_REVISION, APROBADO, etc.)
      try {
        const latestRes = await fetchUltimo(procesoId);
        if (latestRes.data) {
          const estado = (latestRes.data as any).estado;
          setFlujograma(latestRes.data);
          setReadOnly(estado !== 'BORRADOR');
          return;
        }
      } catch {
        // No flowchart at all
      }
      setFlujograma(null);
      setReadOnly(false);
    } catch (err: any) {
      setError(err.message ?? 'Error al cargar flujograma');
      console.error('useFlowData: error loading', err);
    } finally {
      setLoading(false);
    }
  }, [procesoId, cargarVersiones]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  // Update selectedVersionIndex when versions or flujograma change
  useEffect(() => {
    if (versions.length > 0 && flujograma) {
      const idx = versions.findIndex((v) => v.version === flujograma.version);
      if (idx >= 0) setSelectedVersionIndex(idx);
    }
  }, [versions, flujograma]);

  const guardarBorrador = useCallback(
    async (
      nodes: FlowNode[],
      edges: FlowEdge[],
      viewport: Viewport,
      changelog?: string,
      metadata?: Record<string, unknown>,
    ) => {
      if (!flujograma) return;
      try {
        const cleaned = cleanForApi(nodes, edges);
        const res = await actualizarFlujograma(
          flujograma.id,
          {
            nodes: cleaned.nodes as FlowNode[],
            edges: cleaned.edges as FlowEdge[],
            viewport,
            metadata,
          },
          changelog,
        );
        setFlujograma(res.data);
        setReadOnly(false);
      } catch (err: any) {
        throw err; // caller (FlowEditor handleSave) shows toast — don't collapse the editor
      }
    },
    [flujograma],
  );

  const crearFlujograma = useCallback(
    async (
      nodes: FlowNode[],
      edges: FlowEdge[],
      viewport: Viewport,
      metadata?: Record<string, unknown>,
    ) => {
      try {
        const cleaned = cleanForApi(nodes, edges);
        const res = await crearFlujogramaApi(procesoId, {
          nodes: cleaned.nodes as FlowNode[],
          edges: cleaned.edges as FlowEdge[],
          viewport,
          metadata,
        });
        setFlujograma(res.data);
        setReadOnly(false);
        await cargarVersiones();
      } catch (err: any) {
        throw err; // caller shows toast — don't collapse the editor
      }
    },
    [procesoId, cargarVersiones],
  );

  const enviarRevision = useCallback(async () => {
    if (!flujograma) return;
    try {
      const res = await enviarRevisionApi(flujograma.id);
      setFlujograma(res.data);
      await cargarVersiones();
    } catch (err: any) {
      throw err; // caller shows toast — don't collapse the editor
    }
  }, [flujograma, cargarVersiones]);

  const selectVersion = useCallback(
    async (index: number) => {
      if (index < 0 || index >= versions.length) return;
      const v = versions[index];
      setSelectedVersionIndex(index);
      setLoading(true);
      try {
        const res = await fetchVersion(procesoId, v.version);
        setFlujograma(res.data);
        setReadOnly(v.estado !== 'BORRADOR');
      } catch (err: any) {
        setError(err.message ?? 'Error al cargar version');
      } finally {
        setLoading(false);
      }
    },
    [versions, procesoId],
  );

  const nuevaEdicion = useCallback(async () => {
    try {
      const res = await crearNuevaEdicion(procesoId);
      setFlujograma(res.data);
      setReadOnly(false);
      await cargarVersiones();
    } catch (err: any) {
      throw err; // caller shows toast — don't collapse the editor
    }
  }, [procesoId, cargarVersiones]);

  return {
    flujograma,
    loading,
    error,
    readOnly,
    versions,
    selectedVersionIndex,
    guardarBorrador,
    crearFlujograma,
    enviarRevision,
    selectVersion,
    nuevaEdicion,
    recargar: cargar,
  };
}
